"use client";

import { Fragment } from "react";
import { WHATS_INCLUDED_CONFIG } from "./constants";
import type { Feature, FeatureValue } from "./types";

const CheckIcon = ({ color }: { color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`mx-auto h-5 w-5 ${color}`}
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function FeatureValue({
  value,
  isStarter,
}: {
  value: FeatureValue;
  isStarter: boolean;
}) {
  if (value === "check") {
    return (
      <CheckIcon color={isStarter ? "text-[#0F0F0F]" : "text-[#9CA3AF]"} />
    );
  }
  if (value === "empty") {
    return <span className="text-[#D1D5DB]">—</span>;
  }
  return (
    <span
      className={`text-sm ${isStarter ? "text-[#0F0F0F]" : "text-[#6B6B6B]"}`}
    >
      {value}
    </span>
  );
}

function DesktopTable() {
  return (
    <div className="hidden md:block overflow-hidden rounded-2xl border border-[#E8E6E3]">
      <table className="w-full">
        <thead>
          <tr className="bg-[#f7f8fa]">
            <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F0F0F] w-[40%]">
              Feature
            </th>
            <th className="px-4 py-4 text-center text-sm font-semibold text-[#0F0F0F] w-[20%]">
              Starter
            </th>
            <th className="px-4 py-4 text-center w-[20%]">
              <span className="text-sm font-semibold text-[#6B6B6B]">
                Professional
              </span>
            </th>
            <th className="px-4 py-4 text-center w-[20%]">
              <span className="text-sm font-semibold text-[#6B6B6B]">
                Accountant
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {WHATS_INCLUDED_CONFIG.categories.map((category) => (
            <Fragment key={category.id}>
              <tr
                key={`header-${category.id}`}
                className="border-t border-[#E8E6E3]"
              >
                <td
                  colSpan={4}
                  className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] bg-[#f7f8fa]"
                >
                  {category.name}
                </td>
              </tr>
              {category.features.map((feature) => (
                <tr
                  key={feature.id}
                  className="border-t border-[#E8E6E3] hover:bg-[#f2f3f5] transition-colors"
                >
                  <td className="px-6 py-3.5 text-sm text-[#0F0F0F]">
                    {feature.name}
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <FeatureValue value={feature.starter} isStarter={true} />
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <FeatureValue
                      value={feature.professional}
                      isStarter={false}
                    />
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <FeatureValue
                      value={feature.accountant}
                      isStarter={false}
                    />
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards() {
  return (
    <div className="md:hidden space-y-4">
      {WHATS_INCLUDED_CONFIG.categories.map((category) => (
        <div
          key={category.id}
          className="rounded-xl border border-[#E8E6E3] overflow-hidden"
        >
          <div className="px-4 py-3 bg-[#F5F4F2]">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B]">
              {category.name}
            </h3>
          </div>
          <div className="divide-y divide-[#E8E6E3]">
            {category.features.map((feature) => (
              <div key={feature.id} className="px-4 py-3">
                <p className="text-sm text-[#0F0F0F] mb-2">{feature.name}</p>
                <div className="flex gap-4 text-xs">
                  <span className="text-[#0F0F0F]">
                    Starter:{" "}
                    {feature.starter === "check"
                      ? "Yes"
                      : feature.starter === "empty"
                        ? "—"
                        : feature.starter}
                  </span>
                  <span className="text-[#6B6B6B]">
                    Pro:{" "}
                    {feature.professional === "check"
                      ? "Yes"
                      : feature.professional === "empty"
                        ? "—"
                        : feature.professional}
                  </span>
                  <span className="text-[#6B6B6B]">
                    Acc:{" "}
                    {feature.accountant === "check"
                      ? "Yes"
                      : feature.accountant === "empty"
                        ? "—"
                        : feature.accountant}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function WhatsIncluded() {
  return (
    <section className="py-12 md:py-28 bg-white">
      <div className="mx-auto max-w-5xl px-4 md:px-8">
        <div className="text-center max-w-xl mx-auto mb-8 md:mb-12">
          <h2 className="text-2xl md:text-4xl font-medium tracking-[-0.02em] text-[#0F0F0F]">
            {WHATS_INCLUDED_CONFIG.title}
          </h2>
          <p className="mt-2 md:mt-3 text-sm md:text-base text-[#6B6B6B]">
            {WHATS_INCLUDED_CONFIG.subtitle}
          </p>
        </div>

        <DesktopTable />
        <MobileCards />
      </div>
    </section>
  );
}
